import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";

/**
 * Reference design height for store screenshot composition.
 * Width is derived from the capture window aspect so every Google Play / App Store
 * size (9:16 phone, 5:8 tablets, tall Apple) layouts at the same vertical scale,
 * then scales up to the real viewport — rem/cq units stay readable.
 */
export const SCREENSHOT_DESIGN_H = 940;
/** Nested Google Ads phone + legacy callers that need a fixed portrait box. */
export const SCREENSHOT_DESIGN_W = 430;

/**
 * Compose at designH × aspect-matched width, then scale to fill the viewport.
 * Avoids raw “fill” mode where rem type stays tiny on 1080×1920 while the grid grows.
 */
export function screenshotDesignScaleFromViewport(width, height) {
    if (!width || !height) {
        return {
            mode: "fill",
            scale: 1,
            designW: SCREENSHOT_DESIGN_W,
            designH: SCREENSHOT_DESIGN_H,
        };
    }
    const designH = SCREENSHOT_DESIGN_H;
    const designW = Math.max(320, Math.round(designH * (width / height)));
    const scale = height / designH;
    return {
        mode: "phone",
        scale,
        designW,
        designH,
    };
}

/**
 * Fixed viewport shell that lays out children on an aspect-matched design canvas,
 * then scales to the capture window (Google Play + App Store sizes).
 */
export default function ScreenshotDesignFrame({ children, background, bleed = null }) {
    const [frame, setFrame] = useState(() =>
        typeof window === "undefined"
            ? {
                  mode: "fill",
                  scale: 1,
                  designW: SCREENSHOT_DESIGN_W,
                  designH: SCREENSHOT_DESIGN_H,
              }
            : screenshotDesignScaleFromViewport(window.innerWidth, window.innerHeight),
    );

    useEffect(() => {
        const prevHtml = document.documentElement.style.overflow;
        const prevBody = document.body.style.overflow;
        document.documentElement.style.overflow = "hidden";
        document.body.style.overflow = "hidden";
        const apply = () =>
            setFrame(screenshotDesignScaleFromViewport(window.innerWidth, window.innerHeight));
        apply();
        window.addEventListener("resize", apply);
        return () => {
            document.documentElement.style.overflow = prevHtml;
            document.body.style.overflow = prevBody;
            window.removeEventListener("resize", apply);
        };
    }, []);

    return (
        <Box
            data-screenshot-design-outer=""
            sx={{
                position: "fixed",
                inset: 0,
                width: "100%",
                height: "100%",
                minHeight: "100dvh",
                overflow: "hidden",
                background: background || "#0f172a",
                boxSizing: "border-box",
            }}
        >
            {bleed ? (
                <Box
                    aria-hidden
                    sx={{
                        position: "absolute",
                        inset: 0,
                        zIndex: 0,
                        overflow: "hidden",
                    }}
                >
                    {bleed}
                </Box>
            ) : null}
            <Box
                data-screenshot-design-frame={frame.mode}
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    boxSizing: "border-box",
                    overflow: "hidden",
                    zIndex: 1,
                    ...(frame.mode === "phone"
                        ? {
                              width: frame.designW,
                              height: frame.designH,
                              position: "absolute",
                              left: "50%",
                              top: "50%",
                              transform: `translate(-50%, -50%) scale(${frame.scale})`,
                              transformOrigin: "center center",
                          }
                        : {
                              position: "relative",
                              width: "100%",
                              height: "100%",
                          }),
                }}
            >
                {children}
            </Box>
        </Box>
    );
}
